<template>
  <div
    class="lr-list"
    data-scroll-results
  >
    <h2 class="lr-list-title">
      {{ locatorResultsTitle }}
    </h2>
    <p
      v-if="!childcareResults.length"
      class="italic"
    >
      {{ i18n.locatorResultsList.noResults.label }}
    </p>
    <div
      v-for="(result, index) in childcareResults"
      :key="index"
      class="lr-list-result"
    >
      <h3 class="lr-list-result-title">
        <a
          :href="result.childcareResultLink"
          class="lr-list-result-link"
          @click.prevent="manageOtherLanguageModal(result)"
        >
          <div class="lr-list-result-inner-title">
            <arrow-right class="icon" />
            {{ result.childcareResultTitle }}
          </div>

          <p
            v-if="result.childcareResultLocationName"
            class="lr-list-result-location-name"
          >
            {{ i18n.locatorResultsList.location.label }} {{ result.childcareResultLocationName }}
          </p>

          <div
            v-if="result.childcareResultImages && result.childcareResultImages.length"
            class="lr-list-result-images"
            :class="[index % 2 === 0 ? 'is-even' : 'is-odd']"
          >
            <div
              v-for="(resultImage, j) in result.childcareResultImages.slice(0, 4)"
              :key="j"
              class="lr-list-result-image-container"
              :class="[`lr-list-result-image-${j}`, index % 2 === 0 ? 'is-even' : 'is-odd']"
            >
              <img
                :src="resultImage.childcareResultImage"
                :alt="resultImage.childcareResultImageAlt"
                class="lr-list-result-image"
              >
            </div>
          </div>
        </a>
      </h3>
    </div>

    <LanguageNotAvailableModal
      :is-open="languageNotAvailableModalIsOpen"
      :venue="languageNotAvailableModalVenueObject"
      @close="setLanguageNotAvailableModal"
    />
  </div>
</template>

<script>
import LanguageNotAvailableModal from '@/components/language-selector/LanguageNotAvailableModal.vue';
import { trackEvent } from '@/lib/gtm/trackEvent';

export default {
  name: 'KfLocatorResultsList',
  components: {
    LanguageNotAvailableModal,
    ArrowRight: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ArrowRight.vue'),
  },
  props: {
    i18n: {
      type: Object,
      default: () => {},
    },
    initialFormData: {
      type: Object,
      default: () => {}
    },
    locatorResultsTitle: {
      type: String,
      default: '',
    },
    childcareResults: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      languageNotAvailableModalIsOpen: false,
      languageNotAvailableModalVenueObject: {
        url: '',
        id: '',
      },
    };
  },
  computed: {
    trackData() {
      return {
        event: 'childcare_location',
        event_name: 'childcare_location',
        event_category: 'registration',
        event_action: 'chose_childcare_type',
        event_label:  'BSO',
        location: '',
        school: '',
        count_searchresults: this.childcareResults.length,
      };
    },
  },
  methods: {
    setLanguageNotAvailableModal(givenObject) {
      const object = givenObject || {};

      this.languageNotAvailableModalIsOpen = object.isOpen || false;
      this.languageNotAvailableModalVenueObject.url = object.url || '';
      this.languageNotAvailableModalVenueObject.id = object.id || '';
    },
    manageOtherLanguageModal(venue) {
      if (!venue || !Object.keys(venue).length) {
        return;
      }
      const gtmData = {... this.trackData,  school: venue.childcareResultLocationName, location: venue.childcareResultTitle}

      const currentLanguage = document?.documentElement?.lang;
      const url = venue.childcareResultLink;

      if (!url) {
        return;
      }

      // Link click is prevented. Set window location to URL
      function goToUrl() {
        window.location = url;
      }

      // NL? Go directly to URL.
      if (currentLanguage === 'nl') {
        goToUrl();
        trackEvent(gtmData, this.$gtm.enabled());
        return;
      }

      // If URL don't includes location? It's probably NL. Show modal
      if (!url.includes(`/${currentLanguage}/`)) {
        this.setLanguageNotAvailableModal({
          isOpen: true,
          url: url,
          id: venue?.childcareUrlAddition
        });

        return;
      }

      // Else.. Go to URL
      trackEvent(gtmData, this.$gtm.enabled());
      goToUrl();
    },
  },
};
</script>

<style lang="postcss">
.lr-list {
  &-result {
    @apply mb-20;

    &-link {
      @apply flex flex-col no-underline text-primary-bravo;

      &:hover {
        @apply text-black;

        .icon {
          @apply text-black transform translate-x-2;
        }
      }

      .icon {
        @apply text-primary-bravo fill-current mr-4 w-4;
        transition: color 300ms, transform 300ms;
      }
    }

    &-inner-title {
      @apply flex;
    }

    &-images {
      @apply grid mt-6;
      grid-template-columns: 28% 33% 33%;
      grid-template-rows: 55px 55px;
      grid-template-areas:
        'image1 image3 image4'
        'image2 image3 image4';
      gap: 3%;
      grid-row-gap: 10px;

      &.is-even {
        grid-template-columns: 33% 33% 28%;
        grid-template-areas:
          'image1 image2 image3'
          'image1 image2 image4';
      }

      @screen xs {
        grid-template-rows: 65px 65px;
        grid-row-gap: 20px;
      }

      @screen sm {
        grid-template-rows: 90px 90px;
        grid-row-gap: 20px;
      }

      @screen md {
        grid-template-rows: 112px 112px;
        grid-row-gap: 20px;
      }

      @screen lg {
        grid-template-rows: 147px 147px;
        grid-row-gap: 25px;
      }

      @screen xl {
        grid-template-rows: 180px 180px;
        grid-row-gap: 30px;
      }

      @screen 2xl {
        grid-template-rows: 180px 180px;
        grid-row-gap: 40px;
      }
    }

    &-image {
      @apply block object-cover object-center h-full w-full;

      &-0 {
        grid-area: image1;
        @apply w-full h-full;

        &.is-even {
          align-self: flex-start;
        }
      }

      &-1 {
        grid-area: image2;
        @apply w-full h-full;

        &.is-even {
          align-self: flex-end;
        }
      }

      &-2 {
        grid-area: image3;
        @apply w-full h-full;
        align-self: flex-start;
      }

      &-3 {
        grid-area: image4;
        @apply w-full h-full;

        align-self: flex-end;
      }

      &-container {
        @apply overflow-hidden rounded-smd;
      }
    }

    &-location-name {
      @apply text-black text-sm underline ml-8;
    }
  }
}
</style>
